import { useEffect, useState } from "react";

import type { Cast, EnrichPayload, RosterEntry } from "./lib/combatLogParser";

const TOKEN_KEY = "wcl-admin-token";
const USER_KEY = "wcl-admin-user";
const USER_CHANGED_EVENT = "wcl-user-changed";

export interface CurrentUser {
  username: string;
  role: "master" | "admin" | string;
  displayName?: string | null;
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  emitUserChanged();
}

export function setCurrentUser(user: CurrentUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  emitUserChanged();
}

export function getCurrentUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

function emitUserChanged() {
  // Lets the same tab re-render when the user changes. The browser's
  // built-in `storage` event only fires across tabs, not within one.
  window.dispatchEvent(new Event(USER_CHANGED_EVENT));
}

/**
 * React hook for the currently-logged-in user. Re-renders the consuming
 * component whenever login/logout happens, so UI gates like the
 * master-only sidebar link update without a page refresh.
 */
export function useCurrentUser(): CurrentUser | null {
  const [user, setUser] = useState<CurrentUser | null>(() => getCurrentUser());
  useEffect(() => {
    const handler = () => setUser(getCurrentUser());
    window.addEventListener(USER_CHANGED_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(USER_CHANGED_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return user;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const resp = await fetch(path, { ...init, headers });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // not JSON
    }
    throw new ApiError(resp.status, detail);
  }
  return (await resp.json()) as T;
}

/** Convenience: silently swallow AbortError so callers don't have to. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  username: string;
  role: "master" | "admin" | string;
  displayName?: string | null;
}

export interface AuditEntry {
  username?: string;
  action?: string;
  at?: string;
  slug?: string;
  wclCode?: string;
  meta?: Record<string, unknown>;
}

export interface ImportResponse {
  slug: string;
  wclCode: string;
  /** True when the slug came from a pre-existing analysis (dedupe hit). */
  existed?: boolean;
}

export interface ImportScope {
  zoneId: number;
  zoneName: string;
  fightIds: number[];
}

export interface ProbeRaid {
  zoneId: number;
  zoneName: string;
  fightIds: number[];
  fightCount: number;
  bossFightCount: number;
  kills: number;
  wipes: number;
  startTime: number;
  endTime: number;
  /** Slug of an already-imported report for this (code, zone), if any. */
  existingSlug: string | null;
}

export interface ProbeResponse {
  code: string;
  title?: string | null;
  raids: ProbeRaid[];
}

export interface RecentReport {
  slug: string;
  reportTitle?: string;
  zone?: string;
  createdAt?: string;
  /** Absolute time the raid happened (from WCL report.startTime). */
  raidStartedAt?: string | null;
  /** "active" or "archived". Missing on legacy docs - treated as active. */
  status?: string;
  /** True when a combat-log enrichment has been attached. */
  hasEnrichment?: boolean;
}

/** Small pointer stored on the report doc once an enrichment exists. */
export interface EnrichmentPointer {
  generatedAt: string;
  combatCount: number;
  castCount: number;
  parserVersion: string;
}

/** Lightweight per-combat index entry (parent doc). */
export interface EnrichmentCombatMeta {
  id: number;
  label: string;
  encounterId: number | null;
  kill: boolean | null;
  durationS: number;
  playerCount: number;
  castCount: number;
}

/** Full per-combat payload (cast sequences per player). */
export interface EnrichmentCombatData {
  id: number;
  label: string;
  encounterId: number | null;
  kill: boolean | null;
  casts: Record<string, Cast[]>;
}

/** Response of GET /reports/{slug}/enrichment. */
export interface Enrichment {
  slug: string;
  wclCode?: string;
  scopeZoneId?: number | null;
  generatedAt: string;
  parserVersion: string;
  gapSeconds: number;
  roster: Record<string, RosterEntry>;
  combats: EnrichmentCombatMeta[];
  totals: { combatCount: number; bossCombatCount: number; castCount: number };
  combatData: Record<string, EnrichmentCombatData>;
}

export interface EnrichResponse {
  slug: string;
  combatCount: number;
  bossCombatCount: number;
  castCount: number;
}

export type ReportStatus = "active" | "archived";

export interface MetricBlock {
  title: string;
  description?: string;
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
  error?: string;
  note?: string;
  [key: string]: unknown;
}

export interface Fight {
  id: number;
  name?: string;
  encounterID?: number;
  kill?: boolean;
  startTime?: number;
  endTime?: number;
  durationMs?: number;
}

export interface ReportDoc {
  slug: string;
  wclCode: string;
  wclUrl: string;
  reportTitle?: string;
  zone?: string;
  zoneId?: number;
  /** Zone id this report was scoped to (multi-raid imports). Falls back to
   *  `zoneId` for legacy whole-log reports. Used to scope log enrichment. */
  scopeZoneId?: number | null;
  raidId?: string;
  /** Absolute time the raid actually happened (from WCL report.startTime). */
  raidStartedAt?: string | null;
  /** When this analysis was imported by us. */
  createdAt: string;
  fights: Fight[];
  metrics: Record<string, MetricBlock>;
  /** Render order for `metrics`. Firestore maps don't preserve key order, so
   *  the backend ships the intended order as an array. */
  metricOrder?: string[];
  // Per-fight metrics keyed by fight id (as a string). Only present for
  // boss encounters; trash pulls are aggregated into the all-fights view.
  metricsByFight?: Record<string, Record<string, MetricBlock>>;
  /** Present once a combat-log enrichment has been attached. The Combat Log
   *  section is surfaced (and its data fetched) only when this is set. */
  enrichment?: EnrichmentPointer | null;
}

export const api = {
  login: (username: string, password: string) =>
    request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  importReport: (code: string, force = false, scope?: ImportScope) =>
    request<ImportResponse>("/api/admin/import", {
      method: "POST",
      body: JSON.stringify({ code, force, scope }),
    }),
  probeImport: (code: string) =>
    request<ProbeResponse>("/api/admin/import/probe", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  recent: (signal?: AbortSignal, includeArchived = false) =>
    request<RecentReport[]>(
      `/api/admin/recent?include_archived=${includeArchived}`,
      { signal },
    ),
  setReportStatus: (slug: string, status: ReportStatus) =>
    request<{ slug: string; status: ReportStatus }>(
      `/api/admin/reports/${slug}/status`,
      {
        method: "POST",
        body: JSON.stringify({ status }),
      },
    ),
  getReport: (slug: string, signal?: AbortSignal) =>
    request<ReportDoc>(`/api/reports/${slug}`, { signal }),
  getEnrichment: (slug: string, signal?: AbortSignal) =>
    request<Enrichment>(`/api/reports/${slug}/enrichment`, { signal }),
  enrichReport: (slug: string, payload: EnrichPayload) =>
    request<EnrichResponse>(`/api/admin/reports/${slug}/enrich`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteEnrichment: (slug: string) =>
    request<{ slug: string; deleted: boolean }>(
      `/api/admin/reports/${slug}/enrich`,
      { method: "DELETE" },
    ),
  getAuditLog: (
    opts: { user?: string; action?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (opts.user) params.set("user", opts.user);
    if (opts.action) params.set("action", opts.action);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return request<AuditEntry[]>(`/api/admin/audit${qs ? "?" + qs : ""}`, {
      signal,
    });
  },
};
